import { DbFormField } from '@/components/sysadmin/DbFormField';
import { FilterBuilder } from '@/components/sysadmin/FilterBuilder';
import { Button, Card, CardBody, CardHeader, CardTitle, LoadingContainer, Modal } from '@/components/ui';
import { useDbManager } from '@/hooks/useDbManager';
import { dbManagerService, type DbModelDefinition } from '@/services/dbManager.service';
import {
  buildPrismaWhere,
  countFilterRules,
  createEmptyFilterGroup,
  createEqualityFilterGroup,
  deserializeWhere,
  filterSummary,
  parsePrismaWhere,
  serializeWhere,
  type FilterGroup,
} from '@/types/dbFilters';
import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  DatabaseZap,
  Download,
  ExternalLink,
  FileText,
  Filter,
  Hash,
  Layers,
  Link,
  Maximize2,
  PanelLeftClose,
  PanelLeftOpen,
  Pencil,
  Plus,
  RefreshCw,
  Save,
  Table2,
  Trash2,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

// --- Sub-Modo: Relation Modal (Para Arrays) ---
interface ModalState {
  isOpen: boolean;
  targetModel: string;
  filterField: string;
  filterValue: unknown;
  data: unknown[];
  total: number;
  page: number;
  isLoading: boolean;
}

// --- Sub-Modo: Record Detail (Para Objetos Únicos) ---
interface RecordDetailState {
  isOpen: boolean;
  modelType: string;
  data: any;
}

export function DatabaseManagerPage() {
  const { data, models, metadata, isLoading, totalCount, currentPage, pageSize, setCurrentPage, execute, fetchModels } = useDbManager();
  const [searchParams, setSearchParams] = useSearchParams();

  const model = searchParams.get('model') || '';
  const pageParam = searchParams.get('page');
  const queryParam = searchParams.get('q');

  const modelDefinition = useMemo<DbModelDefinition | null>(() => {
    if (!model || !metadata?.models) return null;

    const searchModel = model.trim().toLowerCase();
    return (
      metadata.models.find(
        (item) => item.name.toLowerCase() === searchModel || (item.dbName && item.dbName.toLowerCase() === searchModel)
      ) || null
    );
  }, [metadata, model]);

  const appliedWhere = useMemo(() => deserializeWhere(queryParam), [queryParam]);
  const hasInvalidFilterQuery = Boolean(queryParam) && !appliedWhere;
  const appliedFilterGroup = useMemo(() => parsePrismaWhere(appliedWhere, modelDefinition), [appliedWhere, modelDefinition]);
  const appliedFilterCount = useMemo(() => countFilterRules(appliedFilterGroup), [appliedFilterGroup]);

  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [draftFilterGroup, setDraftFilterGroup] = useState<FilterGroup>(() => parsePrismaWhere(appliedWhere, modelDefinition));

  const [editRecord, setEditRecord] = useState<{
    isOpen: boolean;
    data: any;
    originalData?: any;
    model: string;
    mode: 'edit' | 'create';
  }>({
    isOpen: false,
    data: null,
    model: '',
    mode: 'edit',
  });
  const [isSaving, setIsSaving] = useState(false);

  // Modal State para Arrays
  const [modal, setModal] = useState<ModalState>({
    isOpen: false,
    targetModel: '',
    filterField: '',
    filterValue: null,
    data: [],
    total: 0,
    page: 1,
    isLoading: false,
  });

  // Modal State para Detalle de Objeto Único
  const [recordDetail, setRecordDetail] = useState<RecordDetailState>({
    isOpen: false,
    modelType: '',
    data: null,
  });

  // Sidebar State & Resizing
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem('db-manager-sidebar-width');
    return saved ? parseInt(saved, 10) : 260;
  });
  const [isResizing, setIsResizing] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('db-manager-sidebar-open');
    return saved === null ? true : saved === 'true';
  });

  useEffect(() => {
    localStorage.setItem('db-manager-sidebar-open', isSidebarOpen.toString());
  }, [isSidebarOpen]);

  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  useEffect(() => {
    setDraftFilterGroup(parsePrismaWhere(appliedWhere, modelDefinition));
  }, [appliedWhere, modelDefinition, model]);

  // --- SINCRONIZACIÓN AUTOMÁTICA CON URL ---
  useEffect(() => {
    if (!model) return;
    const page = Number(pageParam) || 1;
    setCurrentPage(page);
    execute(
      {
        model,
        action: 'findMany',
        args: {
          ...(appliedWhere ? { where: appliedWhere } : {}),
          orderBy: { createdAt: 'desc' },
        },
      },
      page
    );
  }, [model, pageParam, queryParam, appliedWhere, execute, setCurrentPage]);

  const startResizing = useCallback(() => setIsResizing(true), []);
  const stopResizing = useCallback(() => {
    setIsResizing(false);
    localStorage.setItem('db-manager-sidebar-width', sidebarWidth.toString());
  }, [sidebarWidth]);

  const resize = useCallback(
    (e: MouseEvent) => {
      if (isResizing) {
        const newWidth = e.clientX - 10;
        if (newWidth > 180 && newWidth < 500) setSidebarWidth(newWidth);
      }
    },
    [isResizing]
  );

  useEffect(() => {
    window.addEventListener('mousemove', resize);
    window.addEventListener('mouseup', stopResizing);
    return () => {
      window.removeEventListener('mousemove', resize);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, [resize, stopResizing]);

  const activeModel = model;

  const handleFetch = (options?: {
    targetModel?: string;
    page?: number;
    where?: Record<string, unknown> | null;
    replaceWhere?: boolean;
  }) => {
    const search = new URLSearchParams(searchParams);
    if (options?.targetModel) search.set('model', options.targetModel);

    // Si la página es 1, la quitamos de la URL para limpieza, de lo contrario la seteamos
    if (options?.page && options.page > 1) {
      search.set('page', String(options.page));
    } else {
      search.delete('page');
    }

    if (options?.replaceWhere) {
      search.delete('q');
      const encodedWhere = serializeWhere(options.where ?? undefined);
      const baseQuery = search.toString();
      const finalQuery = encodedWhere ? (baseQuery ? `${baseQuery}&q=${encodedWhere}` : `q=${encodedWhere}`) : baseQuery;
      setSearchParams(finalQuery);
      return;
    }

    setSearchParams(search);
  };

  const handleModelClick = (newModel: string) => {
    setIsFilterPanelOpen(false);
    handleFetch({ targetModel: newModel, page: 1, where: null, replaceWhere: true });
  };

  const navigateToRelation = (target: string, fieldName: string, value: unknown) => {
    // 1. PRIORIDAD: ¿Existe la tabla exactamente? (Case-sensitive "Key")
    let modelToUse = models.find((m) => m === target);

    if (!modelToUse) {
      // 2. FALLBACK CON LOG: Si no existe, buscamos por aproximación
      console.warn(`🔍 DB Manager: '${target}' no es una tabla exacta. Iniciando re-mapeo...`);
      modelToUse =
        models.find((m) => m.toLowerCase() === target.toLowerCase()) || models.find((m) => m.toLowerCase().includes(target.toLowerCase()));

      if (modelToUse) {
        console.log(`✅ Re-mapeo exitoso: [${target}] -> [${modelToUse}]`);
      }
    }

    const finalModel = modelToUse || target;
    const targetMeta = (metadata as any)?.models?.find((m: any) => m.name.toLowerCase() === finalModel.toLowerCase());
    let realFieldName = fieldName;

    if (targetMeta) {
      // Resolución de campos objeto a campos escalares (Prisma relationFromFields)
      const fieldMeta = targetMeta.fields.find((f: any) => f.name === fieldName);
      if (fieldMeta?.kind === 'object' && fieldMeta.relationFromFields?.[0]) {
        realFieldName = fieldMeta.relationFromFields[0];
        console.log(`🔗 Resolución de campo: ${fieldName} -> ${realFieldName} (vía relationFromFields)`);
      }

      const pkField = targetMeta.fields.find((f: any) => f.isId || f.isPrimary)?.name;
      if (pkField && fieldName === 'id') realFieldName = pkField;
    }

    // const newFilters: Record<string, unknown> = { [realFieldName]: value };
    console.log(`📡 Salto Relacional: ${target} -> ${finalModel} @ ${realFieldName}=${value}`);

    const relationGroup = createEqualityFilterGroup(realFieldName, value);
    const relationWhere = buildPrismaWhere(relationGroup, targetMeta);

    setIsFilterPanelOpen(false);
    handleFetch({
      targetModel: finalModel,
      page: 1,
      where: relationWhere ?? null,
      replaceWhere: true,
    });
  };

  const handleApplyFilters = () => {
    const nextWhere = buildPrismaWhere(draftFilterGroup, modelDefinition);
    handleFetch({ page: 1, where: nextWhere ?? null, replaceWhere: true });
    setIsFilterPanelOpen(false);
  };

  const handleClearFilters = () => {
    setDraftFilterGroup(createEmptyFilterGroup('AND'));
    handleFetch({ page: 1, where: null, replaceWhere: true });
    setIsFilterPanelOpen(false);
  };

  const fetchModalData = useCallback(
    async (targetModel: string, fieldName: string, value: unknown, page: number) => {
      setModal((prev) => ({ ...prev, isLoading: true, targetModel, filterField: fieldName, filterValue: value, page, isOpen: true }));
      const res = await execute(
        {
          model: targetModel,
          action: 'findMany',
          args: {
            where: { [fieldName]: value },
            orderBy: { createdAt: 'desc' },
            take: pageSize,
            skip: (page - 1) * pageSize,
          },
        },
        page
      );

      if (res && res.success) {
        setModal((prev) => ({ ...prev, data: Array.isArray(res.data) ? res.data : [], total: res.total ?? 0, isLoading: false }));
      } else {
        setModal((prev) => ({ ...prev, isLoading: false, data: [] }));
      }
    },
    [execute, pageSize]
  );

  const exportToJSON = () => {
    if (!data || data.length === 0) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${activeModel}_export_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const createColumns = (modelName: string, rowData: unknown[]): ColumnDef<any>[] => {
    const searchName = modelName.trim().toLowerCase();
    const currentModelMeta = (metadata as any)?.models?.find(
      (m: any) => m.name.toLowerCase() === searchName || (m.dbName && m.dbName.toLowerCase() === searchName)
    );

    const findBestModel = (name: string) => {
      const exact = models.find((m) => m === name);
      if (exact) return exact;
      const normalized = name.toLowerCase();
      const match = models.find((m) => m.toLowerCase() === normalized) || models.find((m) => m.toLowerCase().includes(normalized));
      return match || null;
    };

    const columns: ColumnDef<any>[] = [
      {
        id: 'actions_editor',
        header: () => <div className="pl-4 text-[9px] font-black text-gray-400 uppercase">Edit</div>,
        cell: ({ row }: any) => (
          <div className="flex items-center gap-1 justify-start pl-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setEditRecord({
                  isOpen: true,
                  data: { ...row.original },
                  originalData: { ...row.original },
                  model: modelName,
                  mode: 'edit',
                });
              }}
              className="h-9 w-9 p-0 hover:bg-indigo-50 text-indigo-600 rounded-lg transition-all flex items-center justify-center border border-transparent hover:border-indigo-100/50"
              title="Editar Registro"
            >
              <Pencil className="w-4.5 h-4.5" />
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={async (e) => {
                e.stopPropagation();
                const currentModelMeta = (metadata as any)?.models?.find(
                  (m: any) =>
                    m.name.toLowerCase() === modelName.toLowerCase() || (m.dbName && m.dbName.toLowerCase() === modelName.toLowerCase())
                );
                const pkField = currentModelMeta?.fields.find((f: any) => f.isPrimary || f.isId)?.name || 'id';
                const pkValue = row.original[pkField];

                if (
                  window.confirm(`¿Estás seguro de que deseas eliminar este registro (${pkField}: ${pkValue}) de la tabla ${modelName}?`)
                ) {
                  setIsSaving(true);
                  try {
                    const res = await dbManagerService.executeQuery({
                      model: modelName,
                      action: 'delete',
                      args: { where: { [pkField]: pkValue } },
                    });

                    if (res.success) {
                      handleFetch();
                    } else {
                      alert(`Error al eliminar: ${res.error}`);
                    }
                  } catch (err) {
                    alert('Error en el proceso de eliminación');
                  } finally {
                    setIsSaving(false);
                  }
                }
              }}
              className="h-9 w-9 p-0 hover:bg-red-50 text-red-400 hover:text-red-600 rounded-lg transition-all flex items-center justify-center border border-transparent hover:border-red-100/50"
              title="Eliminar Registro"
            >
              <Trash2 className="w-4.5 h-4.5" />
            </Button>
          </div>
        ),
        size: 90,
      },
    ];

    if (!currentModelMeta) {
      if (!rowData || rowData.length === 0) return columns;
      const introspectedCols = Object.keys(rowData[0] as any).map((key) => {
        const potentialModel = findBestModel(key);
        return {
          accessorKey: key,
          header: key,
          cell: (info: any) => {
            const val = info.getValue();
            if (typeof val === 'object' && val !== null) {
              const isArray = Array.isArray(val);
              return (
                <Button
                  onClick={() => {
                    if (isArray) return;
                    setRecordDetail({ isOpen: true, modelType: potentialModel || key, data: val });
                  }}
                  variant="secondary"
                  className="h-7 py-0 px-3 text-[10px] bg-sky-50 border-sky-100 text-sky-700 font-black uppercase"
                >
                  {isArray ? <Layers className="w-3 h-3 mr-1" /> : <FileText className="w-3 h-3 mr-1" />}
                  {isArray ? 'Lista' : `Ver ${potentialModel || key}`}
                </Button>
              );
            }
            return <span className="px-4 text-gray-600 truncate block max-w-xs">{String(val)}</span>;
          },
        };
      });
      columns.push(...introspectedCols);
    } else {
      const fieldsCols = currentModelMeta.fields.map((f: any) => {
        const isRelation = f.kind === 'object';
        const targetModelName = isRelation ? models.find((m) => m.toLowerCase() === f.type.toLowerCase()) || f.type : null;

        return {
          accessorKey: f.name,
          header: () => (
            <div className="flex items-center gap-2">
              {isRelation && (f.isArray ? <Layers className="w-3 h-3 text-indigo-400" /> : <Link className="w-3 h-3 text-sky-400" />)}
              <span className="uppercase tracking-widest">{f.name}</span>
            </div>
          ),
          cell: (info: any) => {
            const value = info.getValue();
            const rowOriginal = info.row.original;

            if (isRelation && f.isArray) {
              const pkField = currentModelMeta.fields.find((field: any) => field.isId || field.isPrimary)?.name || 'id';
              const pkValue = rowOriginal[pkField];
              const targetMeta = (metadata as any)?.models?.find((m: any) => m.name.toLowerCase() === f.type.toLowerCase());
              const backFieldMeta = targetMeta?.fields?.find(
                (tf: any) => tf.type.toLowerCase() === currentModelMeta.name.toLowerCase() && !tf.isArray
              );

              // Si el campo de retorno es un objeto, usamos su campo escalar (relationFromFields) para el filtro
              const backField = (backFieldMeta?.kind === 'object' && backFieldMeta.relationFromFields?.[0]) || backFieldMeta?.name || 'id';

              return (
                <Button
                  onClick={() => navigateToRelation(targetModelName as string, backField, pkValue)}
                  variant="secondary"
                  className="h-7 py-0 px-3 text-[10px] bg-indigo-50 border-indigo-100 text-indigo-700 font-black uppercase tracking-tighter"
                >
                  <Maximize2 className="w-3 h-3 mr-1" /> {f.type}
                </Button>
              );
            }

            if (isRelation && !f.isArray) {
              if (!value) return <span className="text-gray-300 italic text-[10px] px-4 opacity-50">---</span>;

              const targetMeta = (metadata as any)?.models?.find((m: any) => m.name.toLowerCase() === f.type.toLowerCase());
              const pkField = targetMeta?.fields?.find((field: any) => field.isId || field.isPrimary)?.name || 'id';
              const pkValue = typeof value === 'object' && value !== null ? (value as any)[pkField] : value;

              return (
                <Button
                  onClick={() => navigateToRelation(targetModelName as string, pkField, pkValue)}
                  variant="secondary"
                  className="h-7 py-0 px-3 text-[10px] bg-sky-50 border-sky-100 text-sky-700 font-black uppercase tracking-tighter"
                >
                  <FileText className="w-3 h-3 mr-1" /> {f.type}
                </Button>
              );
            }

            if (value === null || value === undefined) return <span className="text-gray-200 px-4">---</span>;
            if (f.type === 'DateTime')
              return <span className="text-[10px] font-medium text-gray-400 px-4">{new Date(value as string).toLocaleDateString()}</span>;
            if (f.type === 'Boolean')
              return (
                <div className="px-4">
                  {value ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <X className="w-4 h-4 text-red-300" />}
                </div>
              );

            return (
              <span
                className={`px-4 truncate block max-w-xs tabular-nums text-gray-600 ${f.name.toLowerCase().includes('id') ? 'text-gray-300 text-[10px]' : 'font-medium'}`}
              >
                {String(value)}
              </span>
            );
          },
        };
      });
      columns.push(...fieldsCols);
    }

    return columns;
  };

  const mainColumns = useMemo(
    () => createColumns(activeModel, data),
    [data, activeModel, metadata, modal.isOpen, recordDetail.isOpen, models]
  );
  const table = useReactTable({ data, columns: mainColumns, getCoreRowModel: getCoreRowModel() });
  const totalPages = Math.ceil(totalCount / pageSize);

  const modalColumns = useMemo(() => createColumns(modal.targetModel, modal.data), [modal.data, modal.targetModel, metadata, models]);
  const modalTable = useReactTable({ data: modal.data, columns: modalColumns, getCoreRowModel: getCoreRowModel() });
  // const modalTotalPages = Math.ceil(modal.total / pageSize);

  return (
    <div className="flex h-[calc(100vh-140px)] overflow-hidden bg-white rounded-xl border border-gray-100 shadow-sm relative font-sans">
      {/* Sidebar */}
      <aside
        style={{ width: isSidebarOpen ? `${sidebarWidth}px` : '0px' }}
        className={`bg-gray-50/20 border-r border-gray-200 flex flex-col shrink-0 transition-[width] duration-300 ease-in-out relative z-30 ${!isSidebarOpen ? 'overflow-hidden' : ''}`}
      >
        <div
          className={`p-4 border-b border-gray-200 flex items-center justify-between bg-white shadow-sm transition-opacity duration-300 ${!isSidebarOpen ? 'opacity-0' : 'opacity-100'}`}
        >
          <h3 className="text-xs font-black text-gray-900 flex items-center gap-2 uppercase tracking-tighter whitespace-nowrap">
            <DatabaseZap className="w-5 h-5 text-indigo-600" />
            Schema
          </h3>
          <button onClick={() => setIsSidebarOpen(false)} className="md:block hidden p-1 hover:bg-gray-100 rounded text-gray-400">
            <PanelLeftClose className="w-4 h-4" />
          </button>
        </div>
        <div
          className={`flex-1 overflow-y-auto p-2 space-y-0.5 scrollbar-thin scrollbar-thumb-gray-200 transition-opacity duration-300 ${!isSidebarOpen ? 'opacity-0' : 'opacity-100'}`}
        >
          {models.sort().map((m) => (
            <button
              key={m}
              onClick={() => handleModelClick(m)}
              className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all flex items-center justify-between group ${
                model === m
                  ? 'bg-indigo-600 text-white font-bold shadow-lg shadow-indigo-100'
                  : 'text-gray-500 hover:bg-indigo-50 hover:text-indigo-600'
              }`}
            >
              <div className="flex items-center gap-2 truncate">
                <Table2 className={`w-4 h-4 ${model === m ? 'text-white' : 'text-gray-300 group-hover:text-indigo-500'}`} />
                <span className="truncate">{m}</span>
              </div>
            </button>
          ))}
        </div>
      </aside>

      {/* Barra de redimensionamiento */}
      <div
        onMouseDown={startResizing}
        className={`w-1.5 cursor-col-resize transition-all z-40 relative group flex items-center justify-center ${isResizing ? 'bg-indigo-600' : 'bg-gray-100 hover:bg-indigo-300'}`}
      >
        <div className="absolute inset-y-0 -left-2 -right-2 z-0" />
        <div className="w-0.5 h-10 bg-gray-300 group-hover:bg-indigo-100 rounded-full z-10" />
      </div>

      {/* Main Content Area */}
      <main className="flex-1 overflow-hidden flex flex-col min-w-0 bg-white">
        <div className="bg-white border-b border-gray-100 px-6 py-4 flex flex-col md:flex-row gap-4 items-center justify-between shadow-sm">
          <div className="flex items-center gap-4 flex-1">
            {!isSidebarOpen && (
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="p-2 hover:bg-indigo-50 text-indigo-600 rounded-xl transition-all shadow-sm border border-indigo-100"
              >
                <PanelLeftOpen className="w-5 h-5" />
              </button>
            )}
            <div className="w-full md:max-w-md">
              <p className="text-xs font-black uppercase tracking-widest text-gray-400">Esquema activo</p>
              <p className="mt-1 text-sm font-semibold text-gray-800 truncate">
                {activeModel || 'Selecciona un esquema en la barra lateral'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 w-full md:w-auto relative">
            <Button
              variant="secondary"
              onClick={() => setIsFilterPanelOpen((prev) => !prev)}
              disabled={!model}
              className="flex-1 md:flex-none h-11 px-6 rounded-xl font-bold border border-gray-200 bg-white hover:bg-gray-50"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filtros
              {appliedFilterCount > 0 && (
                <span className="ml-2 rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-black text-indigo-600">
                  {appliedFilterCount}
                </span>
              )}
            </Button>
            <Button
              onClick={() => {
                setEditRecord({
                  isOpen: true,
                  data: {}, // Datos vacíos para nuevo registro
                  model: model,
                  mode: 'create',
                });
              }}
              disabled={!model}
              className="flex-1 md:flex-none shadow-lg shadow-emerald-100 bg-emerald-600 hover:bg-emerald-700 h-11 px-8 rounded-xl font-bold border border-emerald-500"
            >
              <Plus className="w-5 h-5 mr-2" /> Nuevo Registro
            </Button>
            <Button
              onClick={() => handleFetch()}
              isLoading={isLoading}
              className="flex-1 md:flex-none shadow-lg shadow-indigo-100 bg-indigo-600 hover:bg-indigo-700 h-11 px-8 rounded-xl font-bold"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} /> Refrescar
            </Button>
            <button
              onClick={exportToJSON}
              disabled={!data || data.length === 0}
              className="p-3 border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-30 transition-colors bg-white shadow-sm font-bold group"
            >
              <Download className="w-5 h-5 text-gray-500 group-hover:text-indigo-600 transition-colors" />
            </button>

            {isFilterPanelOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setIsFilterPanelOpen(false)} />
                <Card className="absolute right-0 top-14 z-50 w-[min(920px,calc(100vw-2rem))] border border-gray-200 shadow-2xl">
                  <CardBody className="p-4">
                    <FilterBuilder
                      value={draftFilterGroup}
                      onChange={setDraftFilterGroup}
                      modelDefinition={modelDefinition}
                      enums={metadata?.enums ?? {}}
                      onApply={handleApplyFilters}
                      onClear={handleClearFilters}
                    />
                  </CardBody>
                </Card>
              </>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col p-6 space-y-4">
          <div className="flex-1 overflow-hidden min-h-0 flex flex-col">
            <Card className="flex-1 overflow-hidden flex flex-col shadow-2xl shadow-indigo-100/20 border-gray-100 rounded-2xl relative">
              <CardHeader className="flex flex-row items-center justify-between border-b border-gray-100 bg-white shrink-0 px-6 py-5">
                <CardTitle className="text-sm text-gray-800 flex items-center gap-4">
                  <span className="text-gray-400 font-medium tracking-tight">VISTA ACTUAL:</span>
                  <span className="text-indigo-700 font-black px-4 py-1.5 bg-indigo-50 rounded-xl border border-indigo-100 tracking-tight shadow-sm flex items-center gap-2">
                    <Hash className="w-4 h-4 opacity-40" />
                    {activeModel || 'Seleccione un modelo'}
                  </span>
                </CardTitle>
                {totalCount > 0 && (
                  <div className="flex items-center gap-3">
                    <div className="text-[10px] font-black text-indigo-500 bg-indigo-50/50 px-4 py-2 rounded-full border border-indigo-100 uppercase tracking-widest shadow-inner">
                      {totalCount} REGISTROS
                    </div>
                  </div>
                )}
              </CardHeader>

              {/* ACTIVE FILTERS SECTION */}
              {(appliedFilterCount > 0 || hasInvalidFilterQuery) && (
                <div className="px-6 py-3 bg-gray-50/50 border-b border-gray-100 flex items-center flex-wrap gap-3">
                  <div className="flex items-center gap-2 text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">
                    <Filter className="w-3 h-3" /> Filtros Activos:
                  </div>
                  {hasInvalidFilterQuery ? (
                    <div className="bg-amber-50 border border-amber-200 text-amber-700 text-[11px] font-semibold px-3 py-1.5 rounded-full">
                      El parametro q es invalido y no se pudo aplicar.
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 bg-white border border-indigo-100 px-3 py-1.5 rounded-full shadow-sm">
                      <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-tighter">Resumen:</span>
                      <span className="text-[10px] font-black text-indigo-700 tabular-nums">{filterSummary(appliedFilterGroup)}</span>
                    </div>
                  )}
                  <Button
                    variant="ghost"
                    onClick={handleClearFilters}
                    className="h-7 px-3 text-[10px] font-black text-red-400 hover:text-red-500 hover:bg-red-50 uppercase tracking-widest"
                  >
                    Limpiar Todo
                  </Button>
                </div>
              )}

              <CardBody className="p-0 flex-1 overflow-hidden flex flex-col relative min-h-0">
                {isLoading ? (
                  <LoadingContainer text={`Analizando ${activeModel}...`} />
                ) : (
                  <>
                    <div className="flex-1 overflow-auto w-full scrollbar-thin scrollbar-thumb-gray-200 min-h-0">
                      <table className="w-full text-left text-[11px] border-collapse table-auto tabular-nums">
                        <thead className="bg-white sticky top-0 z-20">
                          {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                              {headerGroup.headers.map((header) => (
                                <th
                                  key={header.id}
                                  className="px-6 py-5 font-black text-gray-400 whitespace-nowrap uppercase tracking-widest text-[9px] bg-white border-b border-gray-100 group-hover:text-indigo-600 transition-colors"
                                >
                                  {flexRender(header.column.columnDef.header, header.getContext())}
                                </th>
                              ))}
                            </tr>
                          ))}
                        </thead>
                        <tbody className="bg-white">
                          {table.getRowModel().rows.map((row) => (
                            <tr
                              key={row.id}
                              className="hover:bg-indigo-50/20 transition-colors group border-b border-gray-100 last:border-b-0"
                            >
                              {row.getVisibleCells().map((cell) => (
                                <td key={cell.id} className="px-2 py-4 whitespace-nowrap">
                                  <div className="max-w-75">{flexRender(cell.column.columnDef.cell, cell.getContext())}</div>
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex items-center justify-between px-8 py-6 bg-white border-t border-indigo-50 shrink-0 select-none">
                      <div className="text-[11px] text-gray-400 font-bold uppercase tracking-widest">
                        {(() => {
                          const start = totalCount === 0 ? 0 : Math.min((currentPage - 1) * pageSize + 1, totalCount);
                          const end = Math.min(currentPage * pageSize, totalCount);
                          if (start > totalCount && totalCount > 0) {
                            return <span className="text-amber-600 font-bold tracking-tight">No hay elementos en esta selección</span>;
                          }
                          return (
                            <>
                              Índice <span className="text-indigo-600 font-black">{start}</span> —{' '}
                              <span className="text-indigo-600 font-black">{end}</span>{' '}
                            </>
                          );
                        })()}
                        <span className="text-gray-300 opacity-50 px-2 italic">de</span>{' '}
                        <span className="text-gray-900 font-black">{totalCount}</span>
                      </div>
                      <div className="flex items-center gap-8">
                        <div className="flex items-center gap-1 bg-gray-50 p-1 rounded-2xl border border-gray-100 shadow-inner">
                          <Button
                            variant="secondary"
                            className="w-10 h-10 p-0 bg-white border-none shadow-md rounded-xl hover:scale-110 active:scale-95 transition-all text-indigo-600"
                            disabled={currentPage <= 1 || isLoading}
                            onClick={() => {
                              handleFetch({ page: currentPage - 1 });
                            }}
                          >
                            <ChevronLeft className="w-5 h-5" />
                          </Button>
                          <div className="px-6 font-black text-sm text-indigo-800 tabular-nums">{currentPage}</div>
                          <Button
                            variant="secondary"
                            className="w-10 h-10 p-0 bg-white border-none shadow-md rounded-xl hover:scale-110 active:scale-95 transition-all text-indigo-600"
                            disabled={currentPage >= totalPages || isLoading}
                            onClick={() => {
                              handleFetch({ page: currentPage + 1 });
                            }}
                          >
                            <ChevronRight className="w-5 h-5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </div>
        </div>
      </main>

      {/* Relation Modal (Sub-Explorer de Arreglos) */}
      <Modal
        isOpen={modal.isOpen}
        onClose={() => setModal((prev) => ({ ...prev, isOpen: false }))}
        title={`Sub-Colección: ${modal.targetModel}`}
        size="xl"
      >
        <div className="flex flex-col h-full -mx-6 -mt-4 -mb-4">
          <div className="px-6 py-4 bg-indigo-50/30 border-b border-indigo-50 shrink-0">
            <p className="text-[10px] text-indigo-400 font-black uppercase tracking-widest flex items-center gap-3">
              Filtro Activo:{' '}
              <span className="text-indigo-700 bg-white px-2 py-0.5 rounded border border-indigo-100">
                {modal.filterField} = {String(modal.filterValue)}
              </span>
            </p>
          </div>

          <div className="flex-1 overflow-hidden flex flex-col relative bg-white">
            {modal.isLoading ? (
              <div className="p-20">
                <LoadingContainer text={`Analizando ${modal.targetModel}...`} />
              </div>
            ) : modal.data.length === 0 ? (
              <div className="p-32 text-center text-gray-300 flex flex-col items-center gap-6">
                <Table2 className="w-20 h-20 opacity-10" />
                <p className="font-bold text-lg uppercase tracking-widest italic opacity-20">Sin registros vinculados</p>
              </div>
            ) : (
              <div className="flex-1 overflow-auto w-full scrollbar-thin scrollbar-thumb-indigo-100">
                <table className="w-full text-left text-[11px] border-collapse table-auto tabular-nums">
                  <thead className="bg-gray-50/80 sticky top-0 z-20">
                    {modalTable.getHeaderGroups().map((hg) => (
                      <tr key={hg.id}>
                        {hg.headers.map((h) => (
                          <th
                            key={h.id}
                            className="px-8 py-5 font-black text-indigo-900/30 whitespace-nowrap uppercase tracking-widest text-[9px] border-b border-indigo-50"
                          >
                            {flexRender(h.column.columnDef.header, h.getContext())}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody className="divide-y divide-gray-50 bg-white">
                    {modalTable.getRowModel().rows.map((row) => (
                      <tr key={row.id} className="hover:bg-indigo-50/20 transition-colors">
                        {row.getVisibleCells().map((cell) => (
                          <td key={cell.id} className="px-8 py-4 whitespace-nowrap border-r border-gray-50/30 last:border-r-0">
                            <div className="max-w-80">{flexRender(cell.column.columnDef.cell, cell.getContext())}</div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Ficha de Registro Modal (Para Objetos Únicos) */}
      <Modal
        isOpen={recordDetail.isOpen}
        onClose={() => setRecordDetail({ isOpen: false, modelType: '', data: null })}
        title={`Ficha de ${recordDetail.modelType}`}
        size="lg"
      >
        <div className="flex flex-col h-full -mx-6 -mt-4 -mb-4">
          <div className="flex-1 overflow-y-auto max-h-[70vh] scrollbar-thin">
            <div className="grid grid-cols-1 divide-y divide-gray-50 border-t border-gray-50">
              {Object.entries(recordDetail.data || {}).map(([key, val]) => {
                if (typeof val === 'object' && val !== null) return null;
                return (
                  <div key={key} className="flex items-center justify-between p-6 hover:bg-gray-50/50 transition-colors group">
                    <span className="text-[11px] font-black text-gray-400 uppercase tracking-widest group-hover:text-indigo-500 transition-colors">
                      {key}:
                    </span>
                    <span className="text-sm font-bold text-gray-800 tabular-nums bg-white px-3 py-1 rounded-lg border border-gray-100 shadow-sm">
                      {String(val)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="p-8 bg-gray-50 border-t border-gray-100 flex justify-end gap-3 shrink-0">
            <Button
              variant="secondary"
              onClick={() => {
                setRecordDetail({ isOpen: false, modelType: '', data: null });
                setEditRecord({
                  isOpen: true,
                  data: { ...recordDetail.data },
                  originalData: { ...recordDetail.data },
                  model: recordDetail.modelType,
                  mode: 'edit',
                });
              }}
              className="h-12 px-6 rounded-xl font-bold text-indigo-600 hover:bg-indigo-50 border-indigo-100 flex items-center gap-2"
            >
              <Pencil className="w-4 h-4" /> Editar Datos
            </Button>
            <Button
              onClick={() => {
                const targetMeta = (metadata as any)?.models?.find(
                  (m: any) => m.name.toLowerCase() === recordDetail.modelType.toLowerCase()
                );
                const pkField = targetMeta?.fields?.find((f: any) => f.isPrimary || f.isId)?.name;
                const idField = pkField || Object.keys(recordDetail.data || {}).find((k) => k.toLowerCase().includes('id')) || 'id';
                const idValue = (recordDetail.data as any)[idField];

                setRecordDetail({ isOpen: false, modelType: '', data: null });
                navigateToRelation(recordDetail.modelType, idField, idValue);
              }}
              className="h-12 px-8 rounded-xl font-black bg-indigo-600 hover:bg-indigo-700 shadow-lg shadow-indigo-200 flex items-center gap-2 text-white"
            >
              Ver Listado <ExternalLink className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de Edición Dinámica (Basado en Esquema) */}
      <Modal
        isOpen={editRecord.isOpen}
        onClose={() => setEditRecord({ isOpen: false, data: null, model: '', mode: 'edit' })}
        title={`${editRecord.mode === 'create' ? 'Nuevo' : 'Editar'} ${editRecord.model}`}
        size="xl"
      >
        <DbRecordForm
          model={editRecord.model}
          mode={editRecord.mode}
          initialData={editRecord.data}
          originalData={editRecord.originalData}
          metadata={metadata}
          onSave={() => {
            setEditRecord({ isOpen: false, data: null, model: '', mode: 'edit' });
            handleFetch();
          }}
          onCancel={() => setEditRecord({ isOpen: false, data: null, model: '', mode: 'edit' })}
        />
      </Modal>
    </div>
  );
}

// --- COMPONENTE AISLADO PARA EL FORMULARIO (Evita re-renders de la página completa) ---
interface DbRecordFormProps {
  model: string;
  mode: 'edit' | 'create';
  initialData: any;
  originalData?: any;
  metadata: any;
  onSave: () => void;
  onCancel: () => void;
}

function DbRecordForm({ model, mode, initialData, originalData, metadata, onSave, onCancel }: DbRecordFormProps) {
  const [formData, setFormData] = useState(initialData || {});
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const meta = metadata as any;
      const currentModelMeta = meta?.models?.find((m: any) => m.name.toLowerCase() === model.toLowerCase());
      const pkField = currentModelMeta?.fields.find((f: any) => f.isPrimary || f.isId)?.name || 'id';

      // Clonamos para no afectar el estado de la UI durante el guardado
      const updateData = { ...formData };

      // SANITIZACIÓN:
      // 1. Eliminamos relaciones (objetos/arrays)
      currentModelMeta?.fields.forEach((f: any) => {
        if (f.kind === 'object') delete updateData[f.name];
      });

      // 2. Eliminamos campos protegidos
      delete updateData[pkField];
      delete updateData.createdAt;
      delete updateData.updatedAt;

      // 3. Limpieza de objetos residuales
      Object.keys(updateData).forEach((key) => {
        if (typeof updateData[key] === 'object' && updateData[key] !== null) delete updateData[key];
      });

      // 4. SOLO ENVIAR CAMBIOS (Dirty checking) - Solo en modo Edición
      if (mode === 'edit' && originalData) {
        Object.keys(updateData).forEach((key) => {
          if (updateData[key] === originalData[key]) {
            delete updateData[key];
          }
        });

        // Si no hay cambios, simplemente cerramos
        if (Object.keys(updateData).length === 0) {
          onCancel();
          return;
        }
      }

      const res = await dbManagerService.executeQuery({
        model: model,
        action: mode === 'create' ? 'create' : 'update',
        args: {
          ...(mode === 'edit' ? { where: { [pkField]: formData[pkField] } } : {}),
          data: updateData,
        },
      });

      if (res.success) {
        onSave();
      } else {
        alert(`Error: ${res.error}`);
      }
    } catch (err) {
      alert('Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full -mx-6 -mt-4 -mb-4">
      <div className="flex-1 p-8 overflow-y-auto scrollbar-thin min-h-75 bg-white">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {(() => {
            const meta = metadata as any;
            const modelMeta = meta?.models?.find((m: any) => m.name.toLowerCase() === model.toLowerCase());

            if (modelMeta) {
              return modelMeta.fields
                .filter((f: any) => f.kind !== 'object' && !f.isArray)
                .map((f: any) => (
                  <DbFormField
                    key={f.name}
                    field={f}
                    value={formData[f.name]}
                    enumValues={meta?.enums ? meta.enums[f.type] : []}
                    onChange={(val) =>
                      setFormData((prev: any) => ({
                        ...prev,
                        [f.name]: val,
                      }))
                    }
                  />
                ));
            }

            return null;
          })()}
        </div>
      </div>

      <div className="p-8 bg-gray-50 border-t border-gray-100 flex justify-end gap-3 sticky bottom-0 shrink-0">
        <Button variant="secondary" onClick={onCancel} className="h-12 px-8 rounded-xl font-bold" disabled={isSaving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          isLoading={isSaving}
          className="h-12 px-8 rounded-xl font-black bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg shadow-indigo-200 flex items-center gap-2"
        >
          <Save className="w-4 h-4" /> Guardar Cambios
        </Button>
      </div>
    </div>
  );
}
